import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { api } from '../../services/api';

interface WorkflowProgressItem {
  id: string;
  orderId: string;
  orderItemId: string | null;
  stepName: string;
  stepDisplayName: string;
  category: string;
  status: 'pending' | 'blocked' | 'ready' | 'in_progress' | 'complete' | 'skipped';
  blockedReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  completedByName: string | null;
}

interface OrderWorkflowProgress {
  orderId: string;
  orderNumber: string;
  overallProgress: {
    completed: number;
    total: number;
    percentage: number;
  };
  steps: WorkflowProgressItem[];
}

interface PartsPullStatus {
  id: string;
  pullType: 'bin' | 'shelves';
  status: 'pending' | 'in_progress' | 'complete' | 'shortage';
  binColor: string | null;
  itemCount: number;
  pulledCount: number;
  shortageCount: number;
  startedAt: string | null;
  completedAt: string | null;
  pulledByName: string | null;
}

interface WorkflowProgressTabProps {
  orderId: string;
  orderStatus: string;
}

export function WorkflowProgressTab({ orderId, orderStatus }: WorkflowProgressTabProps) {
  const [progress, setProgress] = useState<OrderWorkflowProgress | null>(null);
  const [partsPull, setPartsPull] = useState<PartsPullStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [orderId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load workflow progress
      const progressData = await api.get<OrderWorkflowProgress>(`/workflow/orders/${orderId}/progress`);
      setProgress(progressData);

      // Load parts pull status
      const pullData = await api.get<PartsPullStatus[]>(`/orders/${orderId}/parts-pull`);
      setPartsPull(pullData || []);
    } catch (err: any) {
      console.error('Failed to load progress:', err);
      // Don't show error if order is not in production yet
      if (orderStatus !== 'in_production' && orderStatus !== 'shipped' && orderStatus !== 'complete') {
        setError('Progress tracking begins when order is released to production.');
      } else {
        setError('Failed to load workflow progress');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <span className="text-green-500">&#10003;</span>;
      case 'in_progress':
        return <span className="text-blue-500 animate-pulse">&#9679;</span>;
      case 'ready':
        return <span className="text-yellow-500">&#9675;</span>;
      case 'blocked':
        return <span className="text-red-500">&#9888;</span>;
      case 'skipped':
        return <span className="text-gray-400">&#8212;</span>;
      default:
        return <span className="text-gray-300">&#9675;</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      complete: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      ready: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      skipped: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      pending: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
    };
    const labels: Record<string, string> = {
      complete: 'Complete',
      in_progress: 'In Progress',
      ready: 'Ready',
      blocked: 'Blocked',
      skipped: 'Skipped',
      pending: 'Pending',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      parts_pull: 'Parts Pull',
      cutting: 'Cutting',
      track: 'Track',
      fabric: 'Fabric',
      assembly: 'Assembly',
      quality: 'Quality',
      packing: 'Packing',
      shipping: 'Shipping',
    };
    return labels[category] || category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, h:mm a');
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-4xl mb-4">&#128203;</div>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">No workflow progress data available.</p>
      </div>
    );
  }

  // Group steps by category
  const groupedSteps = progress.steps.reduce((acc, step) => {
    const cat = step.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(step);
    return acc;
  }, {} as Record<string, WorkflowProgressItem[]>);

  // Category order
  const categoryOrder = ['parts_pull', 'cutting', 'track', 'fabric', 'assembly', 'quality', 'packing', 'shipping', 'other'];
  const sortedCategories = categoryOrder.filter(cat => groupedSteps[cat]);

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Production Progress</h3>
          <div className="text-right">
            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {progress.overallProgress.percentage}%
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
              ({progress.overallProgress.completed}/{progress.overallProgress.total} steps)
            </span>
          </div>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress.overallProgress.percentage}%` }}
          />
        </div>
      </div>

      {/* Parts Pull Status */}
      {partsPull.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Parts Pull</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {partsPull.map(pull => (
              <div
                key={pull.id}
                className={`p-4 rounded-lg border-2 ${
                  pull.status === 'complete'
                    ? pull.binColor === 'red'
                      ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                      : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                    : pull.status === 'in_progress'
                      ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white uppercase">
                    {pull.pullType === 'bin' ? 'BIN Parts' : 'SHELF Parts'}
                  </span>
                  {getStatusBadge(pull.status)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between">
                    <span>Items Pulled:</span>
                    <span>{pull.pulledCount}/{pull.itemCount}</span>
                  </div>
                  {pull.shortageCount > 0 && (
                    <div className="flex justify-between text-red-600 dark:text-red-400">
                      <span>Shortages:</span>
                      <span>{pull.shortageCount}</span>
                    </div>
                  )}
                  {pull.completedAt && (
                    <div className="flex justify-between mt-1">
                      <span>Completed:</span>
                      <span>{formatDateTime(pull.completedAt)}</span>
                    </div>
                  )}
                  {pull.binColor && (
                    <div className="flex justify-between mt-1">
                      <span>Bin Color:</span>
                      <span className={`font-medium ${pull.binColor === 'red' ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                        {pull.binColor.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workflow Steps by Category */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Workflow Steps</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {sortedCategories.map(category => (
            <div key={category} className="p-4">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                {getCategoryLabel(category)}
              </h4>
              <div className="space-y-2">
                {groupedSteps[category].map(step => (
                  <div
                    key={step.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      step.status === 'complete'
                        ? 'bg-green-50 dark:bg-green-900/20'
                        : step.status === 'in_progress'
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : step.status === 'blocked'
                            ? 'bg-red-50 dark:bg-red-900/20'
                            : 'bg-gray-50 dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getStatusIcon(step.status)}</span>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {step.stepDisplayName || step.stepName}
                        </div>
                        {step.blockedReason && (
                          <div className="text-xs text-red-600 dark:text-red-400">
                            Blocked: {step.blockedReason}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {step.completedAt && (
                        <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                          <div>{formatDateTime(step.completedAt)}</div>
                          {step.completedByName && (
                            <div className="text-xs">{step.completedByName}</div>
                          )}
                        </div>
                      )}
                      {getStatusBadge(step.status)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WorkflowProgressTab;
